import { useState } from "react";
import "../Styles/Login.css";
import "../Styles/Forms.css";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../Services/AuthContext";
import { ArrowIcon } from "../Components/icons/ArrowIcon";
import { Field, FormError } from "../Components/Form/Field";
import { PasswordInput } from "../Components/Form/PasswordInput";
import { SubmitButton } from "../Components/Form/SubmitButton";
import { useSubmit } from "../Hooks/useSubmit";
import Footer from "../Components/Footer";
import { pendingInviteRoute } from "../Services/pendingInvite";
import { BeatspotIcon } from "../Components/icons/BeatspotIcon";


export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  /**
   * Fel som uppstår här i klienten, innan något anrop gjorts.
   *
   * Ett tomt formulär returnerade tidigare tyst: knappen gjorde
   * ingenting och sidan sa inte varför. Nu pekas de tomma fälten ut på
   * samma sätt som serverns fel gör.
   */
  const [localFields, setLocalFields] = useState<Record<string, string>>({});

  const { submit, pending, error, fieldError, formError, clearError } = useSubmit(
    async () => {
      await login({ email, password });
    }
  );

  const clearAll = () => {
    if (error) clearError();
    if (Object.keys(localFields).length > 0) setLocalFields({});
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    const missing: Record<string, string> = {};
    if (!email.trim()) missing.email = "Enter your email address.";
    if (!password) missing.password = "Enter your password.";

    setLocalFields(missing);
    if (Object.keys(missing).length > 0) return;

    // Bara vid ok navigerar vi vidare. Vid fel ska vi stanna kvar och
    // visa det som gick snett.
    const result = await submit();
    if (!result.ok) return;

    // Kom användaren hit från en vänbjudan ska hen tillbaka dit, inte till
    // startsidan. Se pendingInvite.ts.
    navigate(pendingInviteRoute() ?? "/");
  };

  // Kvitto efter en lyckad återställning eller verifiering.
  const notice = (location.state as { notice?: string } | null)?.notice;

  return (
    <main className="login-wrapper">
      <section className="login-content">
        <div className="login-left-container">
          <div className="logo-wrap">
            <BeatspotIcon size={240} />
          </div>
        </div>

        <div className="login-right-container">
          <h2 className="hero-h2">What's playing near you?</h2>

          <Link to="/register" className="hero-h3">
            Join today <ArrowIcon size={28} className={""} />
          </Link>

          <form className="login-form" onSubmit={handleLogin} noValidate>
            {notice && (
              <p className="form-success" role="status">
                {notice}
              </p>
            )}

            <Field label="Email" error={localFields.email ?? fieldError("email")} required>
              {(props) => (
                <input
                  {...props}
                  className="form-input"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    clearAll();
                  }}
                  autoComplete="email"
                  disabled={pending}
                />
              )}
            </Field>

            <Field
              label="Password"
              error={localFields.password ?? fieldError("password")}
              required
            >
              {(props) => (
                <PasswordInput
                  {...props}
                  value={password}
                  onChange={(value) => {
                    setPassword(value);
                    clearAll();
                  }}
                  autoComplete="current-password"
                  disabled={pending}
                />
              )}
            </Field>

            <FormError message={formError} />

            <SubmitButton pending={pending} pendingLabel="Signing in…">
              Sign in
            </SubmitButton>

            <Link className="form-link" to="/forgot-password">
              Forgotten password?
            </Link>
          </form>
        </div>
      </section>

      <Footer />
    </main>
  );
}
